import { ChevronRight } from 'assets/icons/ChevronRight';
import { NavGroupId } from 'enums';
import { MenuItem } from 'interfaces';
import SideNavItem from './SideNavItem';
import { getItemKey, getNavGroupPanelId } from 'utils/navBar/sideNav.util';

interface SideNavGroupProps {
  groupId: NavGroupId;
  label: string;
  items: MenuItem[];
  isExpanded: boolean;
  hasActiveChild: boolean;
  onToggle: () => void;
}

const getSectionTitleClassName = (
  isExpanded: boolean,
  hasActiveChild: boolean
): string => {
  const isActiveCollapsed = hasActiveChild && !isExpanded;
  const colorClassName = isActiveCollapsed
    ? 'text-primary-600 dark:text-primary-300'
    : 'text-gray-400 dark:text-secondary-300';
  return `flex-1 text-left text-[0.65rem] font-semibold uppercase tracking-wider ${colorClassName}`;
};

const SideNavGroup = ({
  groupId,
  label,
  items,
  isExpanded,
  hasActiveChild,
  onToggle
}: SideNavGroupProps) => {
  const panelId = getNavGroupPanelId(groupId);

  return (
    <div className='w-full'>
      <button
        type='button'
        onClick={onToggle}
        aria-expanded={isExpanded}
        aria-controls={panelId}
        className='group w-full flex items-center gap-2 px-3 py-1.5 rounded-md transition-colors duration-200 hover:bg-primary-50 dark:hover:bg-secondary-dark-300'>
        <span className={getSectionTitleClassName(isExpanded, hasActiveChild)}>
          {label}
        </span>
        <ChevronRight
          styles={`w-3 h-3 transition-transform duration-200 text-gray-400 dark:text-secondary-300 ${
            isExpanded ? 'rotate-90' : 'rotate-0'
          }`}
          fill='currentColor'
        />
      </button>

      <div
        id={panelId}
        role='group'
        aria-label={label}
        hidden={!isExpanded}
        className={isExpanded ? 'mt-1 space-y-1' : undefined}>
        {isExpanded &&
          items.map((item) => (
            <SideNavItem
              key={getItemKey(item)}
              text={item.text ?? undefined}
              path={item.path}
              icon={item.icon}
              subMenus={item.subMenus}
              attribAction={item.attribAction}
              isCollapsed={false}
            />
          ))}
      </div>
    </div>
  );
};

export default SideNavGroup;
